// #77 ReasonBox: the required reason of a Hold, Pass, decline, withdrawal, override or cancellation — a Select of the
// reasons the Admin configures (*Reason*, *Decline reason*, *Withdrawal reason*), a notes TextArea (*Rationale*), and in
// the cancellation form *Reporting category* and *Final notes*. The audit sentence is the hint under the reason
// (*Reason stored*). Error: the required field's red edge and alert line, aria-invalid. Blocked (DC-24): every field
// shows its value read-only, aria-disabled, described by the inline reason; mode="view" ("D values when read"): the
// values as text, no controls.

import type { ReactNode } from "react";
import { type ControlProps } from "../core/Button";
import { Field, TextArea } from "../core/Field";
import { Select, type SelectOption } from "../core/Select";
import { cx } from "../lib/cx";
import { G1_LABELS } from "./labels";
import type { Hs } from "../lib/hs";

export interface ReasonBoxCancel {
  /** The reporting categories (the Admin's configuration). */
  categories: readonly SelectOption[];
  category?: string | undefined;
  onCategory?: ((value: string) => void) | undefined;
  finalNotes?: string | undefined;
  onFinalNotes?: ((value: string) => void) | undefined;
}

export type ReasonBoxField = "reason" | "notes" | "category" | "finalNotes";

export interface ReasonBoxProps extends Omit<ControlProps, "hs"> {
  /** The reason list (the Admin's configuration). */
  reasons: readonly SelectOption[];
  reason?: string | undefined;
  onReason?: ((value: string) => void) | undefined;
  notes?: string | undefined;
  onNotes?: ((value: string) => void) | undefined;
  /** The reason field's name: *Reason* (default), *Decline reason*, *Withdrawal reason*. */
  reasonLabel?: string | undefined;
  /** The notes field's name: *Rationale* (default); false draws no notes field. */
  notesLabel?: string | false | undefined;
  /** The audit sentence under the reason (default *Reason stored*). */
  hint?: ReactNode | undefined;
  /** The cancellation form: *Reporting category* and *Final notes*, both required. */
  cancel?: ReasonBoxCancel | undefined;
  /** Validation errors per field (the required sentence). */
  errors?: Partial<Record<ReasonBoxField, ReactNode>> | undefined;
  hs?: Partial<Record<ReasonBoxField, Hs>> | undefined;
  className?: string | undefined;
}

export function ReasonBox({
  reasons, reason, onReason, notes, onNotes, reasonLabel = G1_LABELS.reason, notesLabel = G1_LABELS.rationale,
  hint = G1_LABELS.reasonStored, cancel, errors = {}, hs = {}, can, blocked, mode, className,
}: ReasonBoxProps) {
  if (can === false) return null;
  const shared = { blocked, mode } as const;
  return (
    <div className={cx("mc-rbox", className)}>
      <Field label={reasonLabel} required hint={hint} error={errors.reason} {...shared}>
        <Select options={reasons} value={reason ?? ""} onChange={(value) => onReason?.(value)} hs={hs.reason} />
      </Field>
      {notesLabel === false ? null : (
        <Field label={notesLabel} error={errors.notes} {...shared}>
          <TextArea rows={3} value={notes ?? ""} onChange={(event) => onNotes?.(event.target.value)} hs={hs.notes} />
        </Field>
      )}
      {cancel ? (
        <>
          <Field label={G1_LABELS.reportingCategory} required error={errors.category} {...shared}>
            <Select options={cancel.categories} value={cancel.category ?? ""} onChange={(value) => cancel.onCategory?.(value)} hs={hs.category} />
          </Field>
          <Field label={G1_LABELS.finalNotes} required error={errors.finalNotes} {...shared}>
            <TextArea rows={3} value={cancel.finalNotes ?? ""} onChange={(event) => cancel.onFinalNotes?.(event.target.value)} hs={hs.finalNotes} />
          </Field>
        </>
      ) : null}
    </div>
  );
}
